import { useEffect, useRef } from 'react';
import axios from 'axios';
import {
  fetchStreamToken,
  getExternalVideoStreamUrl,
} from '../../services/externalVideoApi/externalVideoApi';

/** トークン期限切れの何ミリ秒前に更新するか */
const RENEWAL_BUFFER_MS = 5 * 60 * 1000;

/** 更新失敗時のリトライ間隔 */
const RETRY_DELAY_MS = 30 * 1000;

/**
 * JWT ペイロードから exp クレーム（UNIX秒）を取得し ms に変換する。
 * 失敗した場合は null を返す。
 */
export const parseTokenExpiry = (token: string): number | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const parsed: unknown = JSON.parse(window.atob(base64));
    if (typeof parsed !== 'object' || parsed === null) return null;
    const exp = (parsed as Record<string, unknown>)['exp'];
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
};

interface UseStreamTokenRenewalOptions {
  videoId: string | undefined;
  streamUrl: string | null;
  /** 更新完了時のコールバック。新しいストリームURLと退避した再生位置を受け取る */
  onRenewed: (newUrl: string, savedPosition: number) => void;
  /** 現在の再生位置（秒）を取得する関数 */
  getPlayerTime: () => number;
  /** 権限が剥奪された場合（403）に呼ばれるコールバック */
  onPermissionRevoked?: () => void;
}

/**
 * ストリームトークンの自動更新フック。
 *
 * streamUrl の JWT exp を解析して期限切れ RENEWAL_BUFFER_MS 前にタイマーを設定する。
 * タイマー発火時に現在の再生位置を退避してからトークンを更新し、onRenewed を呼ぶ。
 * 更新後は次の期限に合わせてタイマーを再スケジュールする。
 * 更新失敗時: AbortError/キャンセルは無視、4xx は中止、5xx はリトライ（RETRY_DELAY_MS）。
 * リトライは scheduleRef を使わず fetchStreamToken を直接呼び出すことで
 * 期限切れトークンによる delay=0 の無限ループを防ぐ。
 */
export const useStreamTokenRenewal = ({
  videoId,
  streamUrl,
  onRenewed,
  getPlayerTime,
  onPermissionRevoked,
}: UseStreamTokenRenewalOptions): void => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // コールバック系は ref で保持して stale closure を回避する
  const onRenewedRef = useRef(onRenewed);
  const getPlayerTimeRef = useRef(getPlayerTime);
  const onPermissionRevokedRef = useRef(onPermissionRevoked);
  useEffect(() => { onRenewedRef.current = onRenewed; }, [onRenewed]);
  useEffect(() => { getPlayerTimeRef.current = getPlayerTime; }, [getPlayerTime]);
  useEffect(() => { onPermissionRevokedRef.current = onPermissionRevoked; }, [onPermissionRevoked]);

  // 再帰スケジューリング用 ref。関数本体は refs のみ参照するため依存配列は []。
  const scheduleRef = useRef<(vId: string, token: string) => void>(() => {});

  useEffect(() => {
    scheduleRef.current = (vId: string, token: string) => {
      const expiry = parseTokenExpiry(token);
      if (expiry == null) {
        console.warn('[useStreamTokenRenewal] JWT exp の解析に失敗しました。自動更新は無効です。');
        return;
      }

      const delay = expiry - Date.now() - RENEWAL_BUFFER_MS;
      const clampedDelay = Math.max(0, delay);

      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(async () => {
        const savedPosition = getPlayerTimeRef.current();

        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();

        try {
          const { token: newToken } = await fetchStreamToken(vId, abortControllerRef.current.signal);
          const newUrl = getExternalVideoStreamUrl(vId, newToken);
          onRenewedRef.current(newUrl, savedPosition);
          scheduleRef.current(vId, newToken);
        } catch (err) {
          if (axios.isCancel(err) || (err instanceof Error && err.name === 'AbortError')) {
            // アンマウント or 手動中断による abort: 無視
            return;
          }
          if (axios.isAxiosError(err) && (err.response?.status === 403 || err.response?.status === 404)) {
            // 403: 権限剥奪 / 404: 動画が非公開または削除済み → ユーザーに通知
            onPermissionRevokedRef.current?.();
            return;
          }
          if (axios.isAxiosError(err) && err.response && err.response.status < 500) {
            // その他の 4xx はリトライしても解決しないため中止
            console.warn('[useStreamTokenRenewal] クライアントエラーのため自動更新を中止します。', err.response.status);
            return;
          }
          // 5xx またはネットワークエラー: RETRY_DELAY_MS 後に fetchStreamToken を直接リトライ。
          // scheduleRef.current(vId, token) を使うと期限切れトークンで clampedDelay=0 になり
          // 無限ループになるため、ここでは直接 setTimeout → fetchStreamToken のパスを使う。
          console.warn('[useStreamTokenRenewal] トークン更新に失敗しました。リトライします。', err);
          timerRef.current = setTimeout(async () => {
            if (abortControllerRef.current) abortControllerRef.current.abort();
            abortControllerRef.current = new AbortController();
            try {
              const { token: retryToken } = await fetchStreamToken(vId, abortControllerRef.current.signal);
              const retryUrl = getExternalVideoStreamUrl(vId, retryToken);
              onRenewedRef.current(retryUrl, getPlayerTimeRef.current());
              scheduleRef.current(vId, retryToken);
            } catch (retryErr) {
              if (axios.isCancel(retryErr) || (retryErr instanceof Error && retryErr.name === 'AbortError')) {
                return;
              }
              // リトライも失敗: ユーザーに通知して停止
              onPermissionRevokedRef.current?.();
            }
          }, RETRY_DELAY_MS);
        }
      }, clampedDelay);
    };
  }, []);

  useEffect(() => {
    if (!videoId || !streamUrl) return;

    let token: string | null = null;
    try {
      token = new URL(streamUrl).searchParams.get('token');
    } catch {
      return;
    }
    if (!token) return;

    scheduleRef.current(videoId, token);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [videoId, streamUrl]);
};
